// Parses a CSV/TSV file containing bank statement data and returns an array of transactions
export const parseBankStatementCSV = async (
  file,
  fileIndex,
  delimiter = ","
) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        if (!text) {
          throw new Error("Failed to read file content");
        }

        const transactions = parseTransactions(text, delimiter, file.name);
        resolve(transactions.filter((t) => t !== null));
      } catch (error) {
        reject(
          error instanceof Error
            ? error
            : new Error(
                `Unknown error during parsing file #${fileIndex + 1}: ${
                  file.name
                }`
              )
        );
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
};

const parseTransactions = (text, delimiter, fileName) => {
  // Split the file into lines and remove empty lines
  const lines = text.split("\n").filter((line) => line.trim());
  if (lines.length < 2) {
    throw new Error("File must contain at least a header row and one data row");
  }

  // Parse headers and find column indices
  const headerLine = lines[0].endsWith("\r") ? lines[0] + "," : lines[0];
  const headers = headerLine.split(delimiter).map((header) => header.trim()); // Keep empty headers
  const columnIndices = {
    transactionDate: findColumnIndex(headers, COLUMN_MAPPING.transactionDate),
    postDate: findColumnIndex(headers, COLUMN_MAPPING.postDate),
    description: findColumnIndex(headers, COLUMN_MAPPING.description),
    category: findColumnIndex(headers, COLUMN_MAPPING.category),
    type: findColumnIndex(headers, COLUMN_MAPPING.type),
    amount: findColumnIndex(headers, COLUMN_MAPPING.amount),
    debit: findColumnIndex(headers, COLUMN_MAPPING.debit),
    credit: findColumnIndex(headers, COLUMN_MAPPING.credit),
    account: findColumnIndex(headers, COLUMN_MAPPING.account),
  }; // Add a comma if headerLine ends with /r

  // Validate required columns are present
  if (columnIndices.transactionDate === -1 && columnIndices.postDate === -1)
    throw new Error("Transaction date or Post date column not found");
  if (columnIndices.description === -1)
    throw new Error("Description column not found");
  if (
    columnIndices.amount === -1 &&
    (columnIndices.debit === -1 || columnIndices.credit === -1)
  )
    throw new Error(
      "Amount columns not found (need either Amount or both Debit and Credit)"
    );

  const expectedColumnsCount = headers.length; // Get the expected number of columns

  // Parse transactions
  const transactions = lines.slice(1).map((line, index) => {
    let fields = line.split(delimiter).map((field) => field.trim());

    // Check for extra commas after the description column
    if (fields.length > expectedColumnsCount) {
      const descriptionIndex = columnIndices.description;
      const extraCount = fields.length - expectedColumnsCount; // Calculate how many extra columns there are

      // Merge the description with the extra columns
      fields[descriptionIndex] = [
        ...fields.slice(descriptionIndex, descriptionIndex + 1 + extraCount),
      ].join(" "); // Join with a space or any other delimiter you prefer

      // Remove the extra columns
      fields = fields
        .slice(0, descriptionIndex + 1)
        .concat(fields.slice(descriptionIndex + 1 + extraCount));
    }

    // Handle amount based on available columns
    let amount;
    if (columnIndices.amount !== -1) {
      // Single amount column case
      amount = parseAmount(fields[columnIndices.amount]);
    } else {
      // Separate debit/credit columns case
      const debit = parseAmount(fields[columnIndices.debit]);
      const credit = parseAmount(fields[columnIndices.credit]);
      amount = credit - debit; // Credits are positive, debits are negative
    }

    if (isNaN(amount)) {
      throw new Error(`Invalid amount format in row ${index + 2}`);
    }

    // Determine account value
    let account;
    if (columnIndices.account !== -1) {
      account = fields[columnIndices.account];
    } else if (fileName.startsWith("Chase")) {
      account = fileName.split("_")[0];
    } else {
      account = "Unknown"; // Default value if no account info is available
    }

    const transactionDate =
      // If transaction date is not available, use post date
      columnIndices.transactionDate !== -1
        ? parseDate(fields[columnIndices.transactionDate])
        : parseDate(fields[columnIndices.postDate]);

    const transaction = {
      transactionDate,
      description: fields[columnIndices.description],
      amount,
      account,
      category:
        // If category is not available, use type
        columnIndices.category !== -1
          ? fields[columnIndices.category]
          : columnIndices.type !== -1
          ? fields[columnIndices.type]
          : undefined,
      // monthName: getMonthName(transactionDate),
      // year: getYear(transactionDate),
    };

    // skip card payments: where category is blank and amount is positive
    if (
      (transaction.category === undefined ||
        transaction.category === null ||
        transaction.category.trim() === "") &&
      transaction.amount > 0
    ) {
      return null;
    }

    //skip transactions with category name including payment
    if (
      transaction.category &&
      transaction.category.toLowerCase().includes("payment")
    ) {
      return null;
    }

    return transaction;
  });
  return transactions;
};
// Universal column mapping that includes all possible column names across different banks
export const COLUMN_MAPPING = {
  transactionDate: [
    "Transaction Date",
    "Trans Date",
    "Date",
    "Transaction Time",
  ],
  postDate: ["Post Date", "Posted Date", "Posting Date"],
  description: ["Description", "Payee", "Transaction Description", "Merchant"],
  category: ["Category", "Transaction Category", "Type Category"],
  type: ["Type", "Transaction Type"],
  amount: ["Amount", "Transaction Amount", "Amount (USD)"],
  debit: ["Debit", "Withdrawal", "Withdrawals", "Debit Amount", "-"],
  credit: ["Credit", "Deposit", "Deposits", "Credit Amount", "+"],
  account: ["Account", "Card", "Card No."],
};

// Finds the matching column index based on possible column names
const findColumnIndex = (headers, possibleNames) => {
  const normalizedHeaders = headers.map((h) => h.toLowerCase().trim());
  return normalizedHeaders.findIndex((header) =>
    possibleNames.some((name) => header === name.toLowerCase().trim())
  );
};

// Parses a number string, removing currency symbols and commas
const parseAmount = (value) => {
  if (!value || value.trim() === "") return 0;
  return parseFloat(value.replace(/[$,]/g, ""));
};

/**
 * Parses a date string and standardizes it to yyyy-mm-dd format
 * @param {string} dateStr - The date string to parse
 * @returns {string} The standardized date string in yyyy-mm-dd format
 */
const parseDate = (dateStr) => {
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate)) {
    return isoDate.toISOString().split("T")[0]; // Convert to yyyy-mm-dd
  }

  // Handle mm/dd/yyyy format
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const month = parts[0].padStart(2, "0"); // Ensure two digits
    const day = parts[1].padStart(2, "0"); // Ensure two digits
    const year = parts[2];
    return `${year}-${month}-${day}`; // Convert to yyyy-mm-dd
  }

  throw new Error(`Invalid date format: ${dateStr}`);
};

//  Gets the month name from a date string in yyyy-mm-dd format
const getMonthName = (dateStr) => {
  const monthNumber = dateStr.split("-")[1];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[parseInt(monthNumber, 10) - 1];
};

//  Gets the year from a date string in yyyy-mm-dd format
const getYear = (dateStr) => {
  const year = dateStr.split("-")[0];
  return year;
};
